import React, { useState } from 'react';
import { SubmissionQueueModal, SubmissionQueueFile } from './ReviewDashboard';
import { isEditableCompanion } from './revisionRules';

/**
 * The companion decision for a PDF revision (2026-09-01, Fredrik — the
 * revision-scenarios review). A pdf's editable master is its companion
 * docx/pptx/xlsx; the backend refuses a pdf revision that neither hands
 * over a new companion nor attests the editable source is unchanged
 * (COMPANION_DECISION_REQUIRED). Shared by both revision surfaces.
 */
export type CompanionDecision = { kind: 'unchanged' } | { kind: 'new'; fileId: string; fileName: string };

interface CompanionDecisionModalProps {
  contentName: string;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onDecide: (decision: CompanionDecision) => void;
  onClose: () => void;
}

const CompanionDecisionModal: React.FC<CompanionDecisionModalProps> = ({ contentName, getAuthHeaders, onDecide, onClose }) => {
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (picking) {
    return (
      <SubmissionQueueModal
        getAuthHeaders={getAuthHeaders}
        onPick={(file: SubmissionQueueFile) => {
          if (!isEditableCompanion(file.fileName)) {
            setError(`"${file.fileName}" is not an editable working file — the companion must be a docx, pptx or xlsx.`);
            setPicking(false);
            return;
          }
          onDecide({ kind: 'new', fileId: file.fileId, fileName: file.fileName });
        }}
        onClose={() => setPicking(false)}
      />
    );
  }

  return (
    <div className="review-modal-overlay" onClick={onClose}>
      <div className="review-modal" style={{ maxWidth: 540 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ color: '#1a1a1a' }}>Editable working file for this revision</h3>
        <p style={{ fontSize: 13, color: '#666' }}>
          <strong>{contentName}</strong> is a PDF, so its editable master is the attached working file
          (docx/pptx/xlsx). If the revision changed the content, the working file changed too — hand the
          new one over so future automatic fixes edit the right document.
        </p>
        {error && <p role="alert" style={{ fontSize: 13, color: '#dc3545' }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
          <button className="review-btn review-btn-success" onClick={() => { setError(null); setPicking(true); }}>
            Pick the new working file
          </button>
          <button
            className="review-btn review-btn-outline"
            onClick={() => onDecide({ kind: 'unchanged' })}
            title="Only when the PDF changed without the underlying document changing (e.g. a re-export)"
          >
            Editable source is unchanged
          </button>
          <button className="review-btn review-btn-outline" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
};

export default CompanionDecisionModal;
